// BoosterModal.js
import React, { useState, useEffect } from 'react';
import { APP_CONFIG } from '../config/app';
import styled from 'styled-components';
import greenCPU from '../assets/images/greenCPU.png';
import blueCPU from '../assets/images/blueCPU.png';
import yellowCPU from '../assets/images/yellowCPU.png';
import GenericModal from './GenericModal';

// Styled components specific to BoosterModal content
const CPUGrid = styled.div`
  display: flex;
  padding: 0 20px 10px; /* Original padding */
  gap: 15px;
  overflow-x: auto;
  overflow-y: hidden;
  -webkit-overflow-scrolling: touch;
  scroll-behavior: smooth;
  
  scrollbar-width: none; /* Firefox */
  -ms-overflow-style: none; /* IE and Edge */
  &::-webkit-scrollbar {
    display: none; /* Chrome, Safari, Opera */
  }
  
  & > * {
    flex-shrink: 0;
    min-width: 120px;
  }
`;

const CPUCard = styled.div`
  flex: 0 0 auto;
  width: 120px;
  background-color: ${props => props.$selected ? '#e3f2fd' : '#f8f9fa'};
  border: ${props => props.$selected ? '2px solid #2196F3' : '2px solid transparent'};
  border-radius: 12px;
  padding: 10px 8px;
  display: flex;
  flex-direction: column;
  align-items: center;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background-color: ${props => props.$selected ? '#e3f2fd' : '#f0f0f0'};
  }
  
  &:active {
    transform: scale(0.98);
  }
`;

const CPUImage = styled.img`
  width: 90px;
  height: 90px;
  object-fit: contain;
  margin-bottom: 8px;
`;

const InfoSection = styled.div`
  padding: 15px 20px;
  border-top: 1px solid #eee;
`;

const InfoRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
  font-size: 14px;
`;

const InfoLabel = styled.span`
  color: #666;
  display: flex;
  align-items: center;
  gap: 5px;
`;

const InfoValue = styled.span`
  font-weight: 500;
  color: #000;
`;

const QuantityControls = styled.div`
  display: flex;
  align-items: center;
  gap: 15px;
  margin: 15px 0;
`;

const QuantityButton = styled.button`
  background-color: #f0f0f0;
  border: none;
  border-radius: 50%;
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  font-weight: bold;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background-color: #e0e0e0;
  }
  
  &:active {
    transform: scale(0.95);
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const QuantityDisplay = styled.span`
  font-size: 18px;
  font-weight: bold;
  min-width: 30px;
  text-align: center;
`;

const RentButton = styled.button`
  background-color: #000;
  color: white;
  border: none;
  border-radius: 12px;
  font-size: 16px;
  font-weight: 500;
  padding: 15px;
  width: 100%;
  margin-top: 15px;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background-color: #333;
  }
  
  &:active {
    transform: scale(0.98);
  }
  
  &:disabled {
    background-color: #ccc;
    cursor: not-allowed;
  }
`;

const CPUs = [
  { id: APP_CONFIG.BOOSTER_CONFIG.CPUS.GREEN.ID, name: APP_CONFIG.BOOSTER_CONFIG.CPUS.GREEN.NAME, image: greenCPU, dailyProfit: APP_CONFIG.BOOSTER_CONFIG.CPUS.GREEN.DAILY_PROFIT, totalProfit: APP_CONFIG.BOOSTER_CONFIG.CPUS.GREEN.TOTAL_PROFIT, rentPrice: APP_CONFIG.BOOSTER_CONFIG.CPUS.GREEN.RENT_PRICE, rentPeriodDays: APP_CONFIG.BOOSTER_CONFIG.CPUS.GREEN.RENT_PERIOD_DAYS, color: APP_CONFIG.BOOSTER_CONFIG.CPUS.GREEN.COLOR },
  { id: APP_CONFIG.BOOSTER_CONFIG.CPUS.BLUE.ID, name: APP_CONFIG.BOOSTER_CONFIG.CPUS.BLUE.NAME, image: blueCPU, dailyProfit: APP_CONFIG.BOOSTER_CONFIG.CPUS.BLUE.DAILY_PROFIT, totalProfit: APP_CONFIG.BOOSTER_CONFIG.CPUS.BLUE.TOTAL_PROFIT, rentPrice: APP_CONFIG.BOOSTER_CONFIG.CPUS.BLUE.RENT_PRICE, rentPeriodDays: APP_CONFIG.BOOSTER_CONFIG.CPUS.BLUE.RENT_PERIOD_DAYS, color: APP_CONFIG.BOOSTER_CONFIG.CPUS.BLUE.COLOR },
  { id: APP_CONFIG.BOOSTER_CONFIG.CPUS.YELLOW.ID, name: APP_CONFIG.BOOSTER_CONFIG.CPUS.YELLOW.NAME, image: yellowCPU, dailyProfit: APP_CONFIG.BOOSTER_CONFIG.CPUS.YELLOW.DAILY_PROFIT, totalProfit: APP_CONFIG.BOOSTER_CONFIG.CPUS.YELLOW.TOTAL_PROFIT, rentPrice: APP_CONFIG.BOOSTER_CONFIG.CPUS.YELLOW.RENT_PRICE, rentPeriodDays: APP_CONFIG.BOOSTER_CONFIG.CPUS.YELLOW.RENT_PERIOD_DAYS, color: APP_CONFIG.BOOSTER_CONFIG.CPUS.YELLOW.COLOR }
];

const BoosterModal = ({ isOpen, onClose, userData, onRent }) => {
  const [selectedCPU, setSelectedCPU] = useState('blue');
  const [quantity, setQuantity] = useState(1);
  const [isRenting, setIsRenting] = useState(false);
  const [rentStatus, setRentStatus] = useState({ type: '', message: '' });
  
  const currentCPU = CPUs.find(cpu => cpu.id === selectedCPU);
  // Fallback to default config if currentCPU is somehow undefined initially
  const rentPrice = currentCPU?.rentPrice || APP_CONFIG.BOOSTER_CONFIG.RENT_PRICE;
  const rentPeriodDays = currentCPU?.rentPeriodDays || APP_CONFIG.BOOSTER_CONFIG.RENT_PERIOD_DAYS;
  const dailyProfitRate = currentCPU?.dailyProfit || 0;
  const totalProfitRate = currentCPU?.totalProfit || 0;

  const totalPrice = rentPrice * quantity;

  // Reset state when modal is closed/opened
  useEffect(() => {
    if (isOpen) {
      setSelectedCPU('blue'); // Default selection
      setQuantity(1);
      setIsRenting(false);
      setRentStatus({ type: '', message: '' });
    }
  }, [isOpen]);
  
  const handleQuantityChange = (delta) => {
    setQuantity(prev => Math.max(1, prev + delta));
  };
  
  const handleRent = async () => {
    if (!currentCPU) return; // Should not happen if CPUs array is correct

    setIsRenting(true);
    setRentStatus({ type: '', message: '' });

    const powerIncrease = (dailyProfitRate / 24) * quantity; // Assuming dailyProfit is for 24 hours

    const result = await onRent(totalPrice, powerIncrease);

    if (result.success) {
      setRentStatus({ type: 'success', message: 'Miner rented successfully!' });
      setTimeout(() => {
        onClose(); // This will trigger the useEffect above to reset state for next open
      }, 1500);
    } else {
      setRentStatus({ type: 'error', message: result.error || 'Failed to rent miner.' });
      setIsRenting(false); // Only set isRenting to false on error, success closes modal
    }
  };
  
  return (
    <GenericModal
      isOpen={isOpen}
      onClose={onClose}
      title="Booster"
      zIndex={999999}
      containerMaxWidth="400px" // Original max-width
      contentPadding="0" // Content sections handle their own padding
      headerBorder={false} // Booster modal header doesn't have a bottom border in GenericModal by default
    >
      <CPUGrid>
        {CPUs.map((cpu) => (
          <CPUCard
            key={cpu.id}
            $selected={selectedCPU === cpu.id}
            onClick={() => setSelectedCPU(cpu.id)}
          >
            <CPUImage src={cpu.image} alt={cpu.name} />
          </CPUCard>
        ))}
      </CPUGrid>

      <InfoSection>
        <InfoRow>
          <InfoLabel>Rent Period</InfoLabel>
          <InfoValue>{rentPeriodDays} days</InfoValue>
        </InfoRow>
        
        <InfoRow>
          <InfoLabel>Daily profit 💎</InfoLabel>
          <InfoValue>{(dailyProfitRate * quantity).toFixed(5)} {APP_CONFIG.SECONDARY_COIN_NAME}</InfoValue>
        </InfoRow>
        
        <InfoRow>
          <InfoLabel>Total profit 💎</InfoLabel>
          <InfoValue>{(totalProfitRate * quantity).toFixed(4)} {APP_CONFIG.SECONDARY_COIN_NAME}</InfoValue>
        </InfoRow>

        <QuantityControls>
          <QuantityButton
            onClick={() => handleQuantityChange(-1)}
            disabled={quantity <= 1}
          >
            −
          </QuantityButton>
          <QuantityDisplay>{quantity}</QuantityDisplay>
          <QuantityButton onClick={() => handleQuantityChange(1)}>
            +
          </QuantityButton>
        </QuantityControls>

        <InfoRow>
          <InfoLabel>Rent Price</InfoLabel>
          <InfoValue>{totalPrice.toLocaleString()} {APP_CONFIG.PRIMARY_COIN_SYMBOL}</InfoValue>
        </InfoRow>

        {rentStatus.message && (
          <div style={{
            color: rentStatus.type === 'error' ? 'red' : 'green',
            textAlign: 'center',
            marginTop: '10px', // Added margin top for spacing
            marginBottom: '10px'
          }}>
            {rentStatus.message}
          </div>
        )}

        <RentButton
          onClick={handleRent}
          disabled={isRenting || (userData && userData.primary_coin_balance < totalPrice) || !currentCPU}
        >
          {isRenting ? 'Processing...' : 'Rent Miner'}
        </RentButton>
      </InfoSection>
    </GenericModal>
  );
};

export default BoosterModal;
